import { useState } from 'react';
import Modal from 'react-modal';
import { collection, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import {
    Contract,
    Market,
    productTypeConverter,
    Column,
    PRODUCT_TYPE_COLLECTION_NAME,
    LANGUAGE_COLLECTION_NAME,
    languageConverter,
    COUNTRY_COLLECTION_NAME,
    countryConverter,
    CONTRACT_COLLECTION_NAME,
} from 'models';
import {
    InputFromCollection,
    ConfirmProps,
    Confirm,
    customStyles,
} from 'components/settings/recordList';
import { Button } from 'components/button';
import { db, useAuthState } from 'firebaseStore';
import { CountryAndLanguage } from 'components/countryAndLanguage';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { CollectionProvider, useCollectionContext } from 'components/settings/collectionContext';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import './contract.css';
import { useAccount } from 'components/settings/userProfileContext';

Modal.setAppElement('#modal2');

const contractColumns: Column[] = [
    { field: 'name', label: 'Name', required: true },
    { field: 'productTypeIndexId', label: 'Product Type', required: true },
];

export const ContractPage = () => {
    const [user, , tenant] = useAuthState();
    const { contractId } = useParams();
    const docRef = doc(db, CONTRACT_COLLECTION_NAME, contractId || 'contractIdNotProvided');
    const [docA] = useDocumentData(docRef, { idField: 'id' });
    const navigate = useNavigate();
    const collectionRef = collection(db, CONTRACT_COLLECTION_NAME);
    if (contractId && !docA) {
        return <div>{`contract '${contractId}' not found`}</div>;
    }
    if (!user?.email) {
        return <div>Invalid authentication data</div>;
    }
    const onDelete = (latestContract: Contract) => {
        console.warn('onDelete latestContract', latestContract);
        console.warn('onDelete contractId', `'${contractId}'`);
        return deleteDoc(doc(collectionRef, contractId));
    };
    const isEditing = contractId && docA;
    const nextContract = isEditing
        ? new Contract(docA)
        : new Contract({ name: '', productType: '' });
    const onSave = async (newerContract: Contract, next?: boolean) => {
        if (contractId && !docA) {
            console.error('onSave impossible');
            return;
        }
        const missing = contractColumns.find(
            (column) => column.required && !newerContract[column.field]
        );
        if (missing) {
            return Promise.reject(missing);
        }
        if (isEditing && nextContract.equals(newerContract)) {
            next && navigate(`/contract-clauses/${encodeURIComponent(newerContract.id)}`);
            return Promise.resolve();
        }
        const data = { tenantId: tenant, createdBy: user.email };
        const newDocRef = contractId ? docRef : await addDoc(collectionRef, data);
        const finalContract = new Contract({ ...newerContract, ...data, id: newDocRef.id });
        return updateDoc(newDocRef, finalContract.toFirestore()).then(
            () =>
                navigate(
                    next
                        ? `/contract-clauses/${encodeURIComponent(newDocRef.id)}`
                        : `/contract/${encodeURIComponent(newDocRef.id)}`
                ),
            (error) => {
                console.error('ContractPage updateDoc error', error);
            }
        );
    };
    return (
        <CollectionProvider
            collectionName={PRODUCT_TYPE_COLLECTION_NAME}
            converter={productTypeConverter}
        >
            <CollectionProvider
                collectionName={LANGUAGE_COLLECTION_NAME}
                converter={languageConverter}
            >
                <CollectionProvider
                    collectionName={COUNTRY_COLLECTION_NAME}
                    converter={countryConverter}
                >
                    <ContractPageContent
                        onSave={onSave}
                        contract={new Contract(docA || { tenantId: tenant, name: '' })}
                        onDelete={isEditing ? onDelete : undefined}
                    />
                </CollectionProvider>
            </CollectionProvider>
        </CollectionProvider>
    );
};

interface ContractPageContentProps {
    contract: Contract;
    onSave: (_: Contract, __?: boolean) => void;
    onDelete?: (_: Contract) => Promise<void>;
}
export function ContractPageContent({
    contract: _contract,
    onSave,
    onDelete,
}: ContractPageContentProps) {
    const navigate = useNavigate();
    const account = useAccount();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [contract, setContract] = useState(new Contract(_contract));
    const [countryIndexId, setCountryIndexId] = useState<string>('');
    const [languageIndexId, setLanguageIndexId] = useState<string>('');
    const [confirmProps, setConfirmProps] = useState<ConfirmProps>();
    const [, , , languageMap] = useCollectionContext(LANGUAGE_COLLECTION_NAME);
    const [, , , countryMap] = useCollectionContext(COUNTRY_COLLECTION_NAME);
    const onDeleteMarket = (clickedMarket: Market) =>
        setContract(
            new Contract({
                ...contract,
                markets: contract.markets.filter(
                    (market_) =>
                        !(
                            clickedMarket.languageIndexId === market_.languageIndexId &&
                            clickedMarket.countryIndexId === market_.countryIndexId
                        )
                ),
            })
        );

    return (
        <div className="contract">
            <Modal
                isOpen={!!confirmProps}
                onRequestClose={() => setConfirmProps(undefined)}
                style={customStyles(2)}
            >
                {confirmProps ? <Confirm {...confirmProps} /> : null}
            </Modal>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ flexBasis: '373px', flexGrow: '1' }}></div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        fontSize: '34px',
                        flexBasis: '373px',
                        flexGrow: '1',
                    }}
                >
                    Create new document
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexBasis: '373px',
                        flexGrow: '1',
                    }}
                >
                    {onDelete ? (
                        <Button
                            theme="red"
                            style={{ marginRight: '16px', width: '97px' }}
                            onClick={() =>
                                setConfirmProps({
                                    title: `You are about to delete this contract`,
                                    text: `This contract will be deleted, this action cannot be undone. Are you sure you want to continue?`,
                                    onClick: () => onDelete(contract).then(() => navigate('/')),
                                    setModalIsOpen: () => setConfirmProps(undefined),
                                    actionLabel: 'Delete',
                                })
                            }
                        >
                            Delete
                        </Button>
                    ) : (
                        <Button
                            theme="disabled"
                            style={{ marginRight: '16px', width: '97px' }}
                            onClick={() => null}
                            title="Not saved yet"
                        >
                            Delete
                        </Button>
                    )}
                    <Button
                        style={{ marginRight: '16px', width: '97px' }}
                        onClick={() => onSave(contract)}
                    >
                        Save
                    </Button>
                    <Button style={{ width: '97px' }} onClick={() => onSave(contract, true)}>
                        Next
                    </Button>
                </div>
            </div>

            <div className="newContract">
                <div className="contractLabel">Document name*</div>
                <input
                    className="contractInput"
                    value={contract.name}
                    onChange={(e) => {
                        contract.name = e.target.value;
                        setContract(new Contract(contract));
                    }}
                    placeholder=""
                />
                <div className="contractLabel">Product Type*</div>
                <div style={{ backgroundColor: 'white', height: '55px', width: '465px' }}>
                    <InputFromCollection
                        collectionName={PRODUCT_TYPE_COLLECTION_NAME}
                        value={contract.productTypeIndexId}
                        onSelect={(value) => {
                            contract.productTypeIndexId = value || '';
                            setContract(new Contract(contract));
                        }}
                    />
                </div>
                <div className="contractLabel">Countries and Languages</div>
                <Button
                    theme="soft"
                    style={{ width: 'inherit' }}
                    onClick={() => setModalIsOpen(true)}
                >
                    Add a country and a language
                </Button>
                <Modal
                    isOpen={modalIsOpen}
                    style={customStyles()}
                    onRequestClose={() => setModalIsOpen(false)}
                >
                    <CountryAndLanguage
                        countryIndexId={countryIndexId}
                        setCountryIndexId={setCountryIndexId}
                        languageIndexId={languageIndexId}
                        setLanguageIndexId={setLanguageIndexId}
                        onSave={() => {
                            if (!countryIndexId || !languageIndexId) {
                                return;
                            }
                            contract.markets = [
                                ...contract.markets,
                                { countryIndexId, languageIndexId },
                            ];
                            setContract(new Contract(contract));
                            setModalIsOpen(false);
                            setCountryIndexId('');
                            setLanguageIndexId('');
                        }}
                        onCancel={() => setModalIsOpen(false)}
                    />
                </Modal>
                <div
                    className="listRecords"
                    style={{
                        margin: '10px 0 20px 0',
                    }}
                >
                    {account
                        .getMarkets()
                        .concat(contract.markets)
                        .map((market) => (
                            <div
                                className="itemRecord"
                                key={`${market.countryIndexId}-${market.languageIndexId}-${market.main}`}
                            >
                                <div className="itemRecordSection" style={{ paddingLeft: '72px' }}>
                                    <div className="item">
                                        {countryMap.get(market.countryIndexId)?.name}
                                    </div>
                                    <div className="item">
                                        {languageMap.get(market.languageIndexId)?.name}
                                    </div>
                                    <div className="item" style={{ whiteSpace: 'nowrap' }}>
                                        {market.main && 'Main account version'}
                                    </div>
                                </div>
                                {!market.main && (
                                    <div className="itemRecordEdit">
                                        <DeleteOutlineIcon
                                            style={{ cursor: 'pointer', zIndex: 10 }}
                                            onClick={() => onDeleteMarket(market)}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
}
